import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import ExpandableDataTable from "components/admin/ExpandableDataTable";
import StatusBadge from "components/admin/StatusBadge";
import { apiGet } from "lib/apiClient";
import { formatExact, formatExactInText, formatKes, humanizeKey } from "lib/format";

const columnHelper = createColumnHelper();
const PAGE_SIZE = 25;

/** Only the types a partner's own scope can contain. */
const TYPES = [
  "CLIENT_SETTLEMENT",
  "WINNINGS_PAYOUT",
  "TRADE_SETTLEMENT",
  "COMMISSION_CHARGE",
  "MARKET_CANCELLATION",
  "FEE",
  "MARKET_CREATION_COST",
  "MARKET_CREATION_BOND",
  "MARKET_CREATION_BOND_SETTLEMENT",
  "REVERSAL",
];

const TYPE_TONE = {
  CLIENT_SETTLEMENT: "blue",
  WINNINGS_PAYOUT: "green",
  COMMISSION_CHARGE: "blue",
  MARKET_CANCELLATION: "amber",
  REVERSAL: "red",
  FEE: "gray",
};

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-KE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function effectTone(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return "text-gray-500 dark:text-gray-400";
  return n > 0 ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400";
}

/**
 * The explanation the API builds from the rows it actually posted: the sentence, the
 * formula, every input, and the double-entry legs underneath. Nothing is recomputed
 * here, so what a partner reads always matches the ledger exactly.
 */
function MoneyPanel({ item }) {
  const { explanation } = item;
  const inputs = Object.entries(explanation.inputs || {});
  return (
    <div className="flex flex-col gap-4">
      <section>
        <p className="text-sm font-semibold text-navy-700 dark:text-white">{explanation.title}</p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{formatExactInText(explanation.summary)}</p>
        <p className="mt-3 overflow-x-auto whitespace-nowrap rounded-lg bg-gray-50 px-3 py-2 font-mono text-[11px] text-gray-600 dark:bg-white/5 dark:text-gray-300">
          {explanation.formula}
        </p>
      </section>

      {inputs.length > 0 && (
        <section>
          <p className="text-xs font-medium text-muted-foreground">How this figure is made up</p>
          <dl className="mt-2 overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
            {inputs.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-sm last:border-0 dark:border-white/5">
                <dt className="text-gray-600 dark:text-gray-300">{humanizeKey(key)}</dt>
                <dd className="text-right font-semibold text-navy-700 dark:text-white">{formatExact(value)}</dd>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 bg-brand-50 px-3 py-2 text-sm dark:bg-brand-400/10">
              <dt className="font-semibold text-navy-700 dark:text-white">Result</dt>
              <dd className={`text-right font-semibold ${effectTone(item.walletEffect)}`}>{formatExactInText(explanation.result)}</dd>
            </div>
          </dl>
        </section>
      )}

      <section>
        <p className="text-xs font-medium text-muted-foreground">Ledger entries</p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Double-entry: debits and credits always sum to zero.</p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full table-auto border-collapse text-sm">
            <thead>
              <tr className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                <th className="border-b border-gray-100 pb-1 pr-4 text-start dark:border-white/10">Account</th>
                <th className="border-b border-gray-100 pb-1 pr-4 text-start dark:border-white/10">Direction</th>
                <th className="border-b border-gray-100 pb-1 text-end dark:border-white/10">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(item.entries || []).map((entry, i) => (
                <tr key={i} className="border-b border-gray-50 last:border-0 dark:border-white/5">
                  <td className="py-1.5 pr-4 text-navy-700 dark:text-white">{humanizeKey(entry.ownerType)}</td>
                  <td className="py-1.5 pr-4">
                    <span className={entry.direction === "CREDIT" ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}>
                      {entry.direction}
                    </span>
                  </td>
                  <td className="py-1.5 text-end font-semibold text-navy-700 dark:text-white">{formatKes(entry.amount, entry.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(item.marketQuestion || item.reason) && (
        <section className="rounded-xl bg-gray-50 p-3 dark:bg-white/5">
          {item.marketQuestion && (
            <p className="text-sm text-navy-700 dark:text-white">
              <span className="text-xs font-medium text-muted-foreground">Market</span>
              <br />
              {item.marketQuestion}
            </p>
          )}
          {item.reason && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{formatExactInText(item.reason)}</p>}
        </section>
      )}
    </div>
  );
}

export default function MoneyTimelinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [markets, setMarkets] = useState([]);

  const marketId = searchParams.get("marketId") || "";
  const type = searchParams.get("type") || "";

  function setParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  }

  useEffect(() => {
    apiGet("/portal/markets?page=1&pageSize=100")
      .then((res) => setMarkets(res.markets || []))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (marketId) params.set("marketId", marketId);
    if (type) params.set("type", type);
    apiGet(`/portal/money-timeline?${params.toString()}`)
      .then((res) => {
        setItems(res.items || []);
        setTotal(res.total || 0);
      })
      .catch((err) => setError(err.message || "Could not load your money timeline."))
      .finally(() => setLoading(false));
  }, [marketId, type, page]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("at", {
        header: () => <p>WHEN</p>,
        cell: (info) => (
          <div className="min-w-0">
            <p className="whitespace-nowrap text-sm font-semibold text-navy-700 dark:text-white">{formatWhen(info.getValue())}</p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">{info.row.original.explanation.title}</p>
          </div>
        ),
      }),
      columnHelper.accessor("type", {
        header: () => <p>TYPE</p>,
        cell: (info) => <StatusBadge label={humanizeKey(info.getValue())} tone={TYPE_TONE[info.getValue()] || "gray"} />,
      }),
      columnHelper.accessor("marketQuestion", {
        header: () => <p>MARKET</p>,
        cell: (info) => (
          <p className="max-w-[280px] truncate text-sm text-navy-700 dark:text-white" title={info.getValue() || ""}>
            {info.getValue() || "—"}
          </p>
        ),
      }),
      columnHelper.display({
        id: "result",
        header: () => <p>RESULT</p>,
        cell: (info) => (
          <p className={`whitespace-nowrap text-sm font-semibold ${effectTone(info.row.original.walletEffect)}`}>
            {formatExactInText(info.row.original.explanation.result)}
          </p>
        ),
      }),
    ],
    [],
  );

  const selectClass =
    "h-9 rounded-md border border-border bg-transparent px-3 text-sm outline-none dark:bg-input/30 text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-[3px] focus:ring-ring/50";

  return (
    <ExpandableDataTable
      title="Money timeline"
      subtitle="Every movement on your markets and settlements, with the exact arithmetic behind each amount."
      columns={columns}
      data={items}
      loading={loading}
      error={error}
      onRetry={load}
      emptyMessage="No money movements yet."
      page={page}
      pageSize={PAGE_SIZE}
      total={total}
      onPageChange={setPage}
      renderDetail={(item) => <MoneyPanel item={item} />}
      headerActions={
        <>
          <select value={marketId} onChange={(e) => setParam("marketId", e.target.value)} className={selectClass}>
            <option value="">All markets</option>
            {markets.map((m) => (
              <option key={m.id} value={m.id}>
                {m.question}
              </option>
            ))}
          </select>
          <select value={type} onChange={(e) => setParam("type", e.target.value)} className={selectClass}>
            <option value="">All types</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {humanizeKey(t)}
              </option>
            ))}
          </select>
        </>
      }
    />
  );
}
